/**
 * Equipment Selection Tab Helper Functions
 * Domain normalization, data transformation, and summary calculations
 */

// Normalization function to map any category label to canonical domain keys
export const normalizeDomain = (category) => {
  if (!category) return 'misc';
  
  const categoryMap = {
    // Fire Fighting Equipment
    'FFE': 'ffe',
    'Fire Extinguisher': 'ffe',
    'Fire Fighting Equipment': 'ffe',
    
    // Life Saving Appliances
    'LSA': 'lsa',
    'Life-Saving Appliances': 'lsa',
    'Life Saving Appliances': 'lsa',
    'Safety Equipment': 'lsa',
    
    // Navigation Equipment
    'Navigation': 'navigation',
    'Navigation Equipment': 'navigation',
    'Additional Navigation Equipment': 'navigation',
    'NAV': 'navigation',
    
    // GMDSS/Communications
    'GMDSS': 'gmdss',
    'Communications': 'gmdss',
    'Distress Alerting': 'gmdss',
    'Radio': 'gmdss',
    'Radio Equipment': 'gmdss',
    'Personal Distress': 'gmdss',
    'MSI Reception': 'gmdss',
    'GMDSS Operations': 'gmdss',
    'Search & Rescue': 'gmdss',
    
    // Medical Equipment
    'Medical': 'medical',
    'Medical Equipment': 'medical',
    'Medical Stores': 'medical',
    'Medical Guides': 'medical',
    'MED': 'medical',
    
    // Electrical Systems
    'Electrical': 'electrical',
    'Electrical Systems': 'electrical',
    'Electrical Equipment': 'electrical',
    'Additional Electrical Systems': 'electrical',
    'ELEC': 'electrical',
    
    // Machinery
    'Machinery': 'machinery',
    'Machinery Equipment': 'machinery',
    'Engineering': 'machinery',
    'MACH': 'machinery',
    
    // Operations Equipment
    'Operations': 'operations',
    'Operations Equipment': 'operations',
    'Towing Equipment': 'operations',
    'Deck Equipment': 'operations',
    'Special Equipment': 'operations',
    'Special Operations': 'operations',
    'Dangerous Goods': 'operations',
    'Survey Planning': 'operations',
    
    // Environmental
    'Environmental': 'environmental',
    'Environmental Equipment': 'environmental',
    'Pollution Prevention': 'environmental',
    'ENV': 'environmental',
    
    // General/Other categories
    'General': 'misc',
    'Other': 'misc',
    
    // Crew-related (not equipment but may appear)
    'Crew Certification': 'crew',
    'Crew': 'crew',
    'Familiarization': 'crew',
    'Hours of Rest': 'crew',
    'Medical Fitness': 'crew',
    'Manning Documents': 'crew',
    'Master Cat 0': 'crew',
    'Master Cat 1': 'crew',
    'Master Cat 2': 'crew',
    'Master Cat 3': 'crew',
    'Master Cat 4': 'crew',
    'Master Cat 5': 'crew',
    'Master Cat 6': 'crew',
    'Second Person': 'crew'
  };
  
  // Check exact match first
  const exactMatch = categoryMap[category];
  if (exactMatch) return exactMatch;

  // Check case-insensitive match
  const lowerCategory = category.toLowerCase();
  for (const [key, value] of Object.entries(categoryMap)) {
    if (key.toLowerCase() === lowerCategory) {
      return value;
    }
  }

  // Unknown/custom category — convert to a stable slug so it gets its own group
  // e.g. "Rope Register" → "rope_register", "Lifting Equipment" → "lifting_equipment"
  return category.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') || 'misc';
};

// Domain labels for display - hybrid approach: keep industry standard abbreviations, expand unclear ones
export const DOMAIN_LABELS = {
  ffe: 'FFE',              // Fire Fighting Equipment (industry standard)
  lsa: 'LSA',              // Life Saving Appliances (industry standard)
  navigation: 'NAV',       // Navigation (clear abbreviation)
  gmdss: 'GMDSS',          // Global Maritime Distress (industry standard)
  machinery: 'Machinery',  // Expand - clearer than MACH
  electrical: 'Electrical', // Expand - clearer than ELEC
  medical: 'Medical',      // Expand - clearer than MED
  environmental: 'Environmental', // Expand - clearer than ENV
  operations: 'Operations', // Expand - clearer than OPS
  crew: 'Crew',            // Simple word, no abbreviation needed
  misc: 'Misc'             // Keep short
};

// Group requirements by domain
export const groupRequirementsByDomain = (requirements) => {
  if (!requirements) return {};

  // Flatten all equipment arrays
  const allEquipment = [
    ...(requirements.basicEquipment || []),
    ...(requirements.equipment || []),
    ...(requirements.areaCategoryEquipment || [])
  ];

  // DEBUG: Check for gas detector specifically
  const gasDetectorInAll = allEquipment.find(e => e.id === 'ffe_detector_gas_galley');

  // Group by domain
  const groups = {};
  
  allEquipment.forEach(item => {
    // Normalize category to canonical domain
    const domain = normalizeDomain(item.category);
    
    if (!groups[domain]) {
      groups[domain] = {
        required: [],
        optional: []
      };
    }

    // Add selection state and normalized domain
    const equipmentItem = {
      ...item,
      domain: domain,                     // Add normalized domain to item
      selected: item.mandatory || false, // Required items pre-selected
      selectable: !item.mandatory,       // Required items not selectable
      presence: 'present',               // Default presence state
      qtyRequired: item.mandatory ? (item.quantity || 1) : 0, // Min required quantity
      qtyPresent: item.mandatory ? (item.quantity || 1) : 0,  // Current quantity
      source: 'requirements'             // Source tracking
    };

    if (item.mandatory) {
      groups[domain].required.push(equipmentItem);
    } else {
      groups[domain].optional.push(equipmentItem);
    }
  });

  // DEBUG: Check for gas detector in grouped FFE required
  const gasDetectorInGroups = groups?.ffe?.required?.find(e => e.id === 'ffe_detector_gas_galley');

  return groups;
};

// Calculate selection summary
export const calculateSelectionSummary = (domainGroups) => {
  const summary = {
    totalRequired: 0,
    totalOptionalSelected: 0,
    totalWizardSelected: 0,
    totalSelected: 0,
    totalQuantityRequired: 0,
    totalQuantitySelected: 0,
    byDomain: {}
  };

  Object.keys(domainGroups).forEach(domain => {
    const requiredCount = domainGroups[domain].required?.length || 0;
    const requiredQuantity = (domainGroups[domain].required || [])
      .filter(item => item.presence !== 'overridden')
      .reduce((sum, item) => sum + (item.qtyPresent || item.qtyRequired || 1), 0);
    
    const optionalSelected = (domainGroups[domain].optional || [])
      .filter(item => item.selected).length;
    const optionalQuantity = (domainGroups[domain].optional || [])
      .filter(item => item.selected)
      .reduce((sum, item) => sum + (item.qtyPresent || 1), 0);

    const wizardSelected = (domainGroups[domain].wizardSelected || [])
      .filter(item => item.selected).length;
    const wizardQuantity = (domainGroups[domain].wizardSelected || [])
      .filter(item => item.selected)
      .reduce((sum, item) => sum + (item.qtyPresent || 1), 0);
    
    summary.totalRequired += requiredCount;
    summary.totalOptionalSelected += optionalSelected;
    summary.totalWizardSelected += wizardSelected;
    summary.totalQuantityRequired += requiredQuantity;
    summary.totalQuantitySelected += optionalQuantity + wizardQuantity;
    
    summary.byDomain[domain] = {
      required: requiredCount,
      optionalSelected: optionalSelected,
      wizardSelected: wizardSelected,
      total: requiredCount + optionalSelected + wizardSelected,
      requiredQuantity: requiredQuantity,
      optionalQuantity: optionalQuantity,
      wizardQuantity: wizardQuantity,
      totalQuantity: requiredQuantity + optionalQuantity + wizardQuantity
    };
  });

  summary.totalSelected = summary.totalRequired + summary.totalOptionalSelected + summary.totalWizardSelected;
  summary.totalQuantity = summary.totalQuantityRequired + summary.totalQuantitySelected;
  return summary;
};

// Transform selections to equipment register format
export const transformSelectionsToRegister = (domainGroups, currentVesselId) => {
  const selectedItems = [];
  const auditEntries = [];
  
  Object.keys(domainGroups).forEach(domain => {
    // Add required items (present or overridden)
    domainGroups[domain].required?.forEach(item => {
      const registerItem = {
        id: `reg_${currentVesselId}_${item.id}`,
        name: item.name,
        category: item.category,
        domain: domain,
        source: item.source,
        presence: item.presence,
        qtyRequired: item.qtyRequired,
        qtyPresent: item.presence === 'overridden' ? 0 : item.qtyPresent,
        qtyAdditional: Math.max((item.qtyPresent || 0) - (item.qtyRequired || 0), 0),
        required: true,
        sourceRequirementId: item.id,
        reference: item.reference,
        description: item.description,
        addedAt: new Date().toISOString(),
        addedBy: 'equipment_selection'
      };

      // Add override data if present
      if (item.override) {
        registerItem.override = item.override;
        
        // Create audit entry for override
        auditEntries.push({
          action: 'override_requirement',
          itemId: item.id,
          domain: domain,
          category: item.category,
          reasonCode: item.override.reasonCode,
          details: item.override.details,
          expiresAt: item.override.expiresAt,
          by: item.override.createdBy,
          at: item.override.createdAt
        });
      }

      selectedItems.push(registerItem);
    });

    // Add selected optional items
    domainGroups[domain].optional?.forEach(item => {
      if (item.selected) {
        selectedItems.push({
          id: `reg_${currentVesselId}_${item.id}`,
          name: item.name,
          category: item.category,
          domain: domain,
          source: item.source,
          presence: item.presence,
          qtyRequired: item.qtyRequired,
          qtyPresent: item.qtyPresent || 1,
          qtyAdditional: Math.max((item.qtyPresent || 1) - (item.qtyRequired || 0), 0),
          required: false,
          sourceRequirementId: item.id,
          reference: item.reference,
          description: item.description,
          addedAt: new Date().toISOString(),
          addedBy: 'equipment_selection'
        });
      }
    });

    // Add selected wizard items
    domainGroups[domain].wizardSelected?.forEach(item => {
      if (item.selected) {
        selectedItems.push({
          id: `reg_${currentVesselId}_${item.id}`,
          name: item.name,
          category: item.category || 'Wizard Selected',
          domain: domain,
          source: item.source || 'wizard',
          presence: item.presence || 'present',
          qtyRequired: item.qtyRequired || 0,
          qtyPresent: item.qtyPresent || 1,
          qtyAdditional: Math.max((item.qtyPresent || 1) - (item.qtyRequired || 0), 0),
          required: item.required || false,
          sourceRequirementId: item.originalId || item.id,
          reference: item.reference,
          description: item.description,
          reason: item.reason,
          wizardStep: item.wizardStep,
          addedAt: new Date().toISOString(),
          addedBy: 'wizard_selection'
        });
      }
    });
  });

  return { selectedItems, auditEntries };
};